/**
 * Story 113 (R-G, S120/S133) — the SINGLE point that resolves the two action buttons of the four
 * station widgets: "Active Jobs Detail" (master-detail), "Active jobs" (legacy compact),
 * "Active Single Job" (single-job panel) and "Operation" (assembly).
 *
 * Why it exists: the four widgets compute their gates SEPARATELY today -- the same formula written
 * four times, already drifting (the legacy compact one folds the quantity check into the finish
 * button differently from the other three). This story adds one more rule to that layout, so
 * repeating it a fifth time is how the four surfaces start disagreeing about what the operator can
 * do. The rule is resolved here and tested once (`__tests__/jobModeActions.test.js`).
 *
 * FIXED SEMANTICS of the right-hand button (RN38): "close what I am doing right now". In preparation
 * that means closing the preparation (go to execution); in execution it means closing the job
 * (finish). The button therefore changes ACTION AND LABEL, not just its disabled state -- which is
 * exactly why it cannot be a `disabled` flag on the finish button.
 */
import { JOB_EXECUTION_MODE, resolveJobExecutionMode } from './reportOutcome'

export const JOB_WIDGET_ACTION = {
  PAUSE: 'PAUSE',
  FINISH: 'FINISH',
  GO_TO_EXECUTION: 'GO_TO_EXECUTION',
  CHANGE_STATUS: 'CHANGE_STATUS'
}

const LABEL = {
  [JOB_WIDGET_ACTION.PAUSE]: 'Pause',
  [JOB_WIDGET_ACTION.FINISH]: 'Finish',
  [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: 'Go to execution',
  [JOB_WIDGET_ACTION.CHANGE_STATUS]: 'Change status'
}

const button = (action, disabled, tooltip) => ({
  action,
  label: LABEL[action],
  disabled: Boolean(disabled),
  tooltip: tooltip || ''
})

/**
 * @param {Object} [params]
 * @param {boolean} [params.managePreparation=false] C5 for an active job. It is the switch, not the
 *        code, that decides whether this story is visible at all (RN43/POL5): with it off the layout
 *        is today's, whatever the code says -- which is also the mandatory exit rule of S132, so the
 *        operator is never trapped in a mode the UI cannot represent.
 * @param {string} [params.statusCode=''] C4. Read through `resolveJobExecutionMode`, so absent, null
 *        and any UNKNOWN active code all mean execution: preparation is never inferred by
 *        elimination (the red rule of section 8.1).
 * @param {boolean} [params.finishDisabled=false] The composite finish gate of today (pending parts,
 *        `canFinish`, auto-report), computed by each widget as it always was.
 * @param {boolean} [params.pauseDisabled=false] Today's pause gate.
 * @param {string} [params.gatedTooltip=''] The reason of a PRE-EXISTING gate (acceptance form,
 *        external circuit). It accumulates and takes precedence: preparation never relaxes it, and
 *        never steals its explanation (S130/RN47/RN48).
 * @param {string} [params.finishDisabledTooltip=''] Why finish is disabled, when the widget has its
 *        own message for it. Deliberately NOT carried over to Go to execution (S122).
 * @param {boolean} [params.modeTransitionBusy=false] A mode transition is already in flight
 *        (`useJobModeTransition().busy`). Only "Go to execution" carries it -- it is the one button
 *        that fires the request straight from the click, with no dialog in between. It is resolved
 *        here, and not in each widget, for the same reason the rest of the layout is: four copies of
 *        a rule are four chances to disagree. No tooltip: nothing is refused, the click is simply
 *        already on its way.
 * @returns {{left: Object, right: Object}} Each side: `{ action, label, disabled, tooltip }`.
 */
export const resolveJobModeActions = ({
  managePreparation = false,
  statusCode = '',
  finishDisabled = false,
  pauseDisabled = false,
  gatedTooltip = '',
  finishDisabledTooltip = '',
  modeTransitionBusy = false
} = {}) => {
  const gated = Boolean(gatedTooltip)
  const inPreparation = managePreparation === true &&
    resolveJobExecutionMode(statusCode) === JOB_EXECUTION_MODE.PREPARATION

  if (inPreparation) {
    return {
      left: button(JOB_WIDGET_ACTION.PAUSE, pauseDisabled, gatedTooltip),
      // S121/S122: Go to execution is ALWAYS enabled -- the quantity rule belongs to finishing, not
      // to this. It occupies the finish button's position, so inheriting its disabled state or its
      // reason message is the concrete risk, and neither is carried over. A pre-existing gate still
      // wins, because preparation never relaxes one (S130).
      right: button(JOB_WIDGET_ACTION.GO_TO_EXECUTION, gated || modeTransitionBusy, gatedTooltip)
    }
  }

  if (managePreparation === true) {
    return {
      // In execution the left button becomes the door to the two mode actions (RN41): pause the work
      // or go back to preparation. It is gated exactly like the pause it replaces.
      left: button(JOB_WIDGET_ACTION.CHANGE_STATUS, pauseDisabled, gatedTooltip),
      right: button(JOB_WIDGET_ACTION.FINISH, finishDisabled, gatedTooltip || finishDisabledTooltip)
    }
  }

  // S129/S132: the switch off is today, exactly. Same labels, same positions, same enablement.
  return {
    left: button(JOB_WIDGET_ACTION.PAUSE, pauseDisabled, gatedTooltip),
    right: button(JOB_WIDGET_ACTION.FINISH, finishDisabled, gatedTooltip || finishDisabledTooltip)
  }
}
